import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, User, Phone, FileText, CreditCard } from 'lucide-react'
import { AgentAPI } from '../services/api'

const AddPassenger: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    full_name: '',
    passport_no: '',
    passport_expiry: '',
    cnic: '',
    date_of_birth: '',
    gender: 'male',
    mobile_number: '',
    whatsapp_number: '',
    city: '',
    room_type: 'quad',
    mehram_name: '',
    mehram_relationship: '',
    sale_price: 0,
    amount_paid: 0,
    special_requirements: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.full_name.trim()) {
      setError('Full name is required')
      return
    }

    setSaving(true)
    try {
      await AgentAPI.addPassenger(parseInt(id!), formData)
      navigate(`/allocation/${id}`)
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add passenger')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/allocation/${id}`)}
            className="p-2 -ml-2 text-gray-600"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Add Passenger</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-4 pb-24">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Personal Info */}
        <div className="bg-white rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2 text-gray-700 font-medium mb-2">
            <User size={18} />
            Personal Information
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Full Name *</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="As per passport"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Gender</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Date of Birth</label>
              <input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">City</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              placeholder="e.g., Lahore"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2 text-gray-700 font-medium mb-2">
            <Phone size={18} />
            Contact Details
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Mobile Number</label>
            <input
              type="tel"
              value={formData.mobile_number}
              onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
              placeholder="03xx-xxxxxxx"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">WhatsApp Number</label>
            <input
              type="tel"
              value={formData.whatsapp_number}
              onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
              placeholder="Same as mobile if same"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2 text-gray-700 font-medium mb-2">
            <FileText size={18} />
            Documents
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Passport Number</label>
            <input
              type="text"
              value={formData.passport_no}
              onChange={(e) => setFormData({ ...formData, passport_no: e.target.value.toUpperCase() })}
              placeholder="AB1234567"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Passport Expiry</label>
            <input
              type="date"
              value={formData.passport_expiry}
              onChange={(e) => setFormData({ ...formData, passport_expiry: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">CNIC (13 digits)</label>
            <input
              type="text"
              value={formData.cnic}
              onChange={(e) => setFormData({ ...formData, cnic: e.target.value.replace(/[^0-9]/g, '').slice(0, 13) })}
              placeholder="3520212345678"
              maxLength={13}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Package */}
        <div className="bg-white rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2 text-gray-700 font-medium mb-2">
            <CreditCard size={18} />
            Package & Payment
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Room Type</label>
            <div className="grid grid-cols-4 gap-2">
              {(['quad', 'triple', 'double', 'single'] as const).map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormData({ ...formData, room_type: type })}
                  className={`py-3 rounded-xl text-sm font-medium transition-colors ${
                    formData.room_type === type
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Sale Price (PKR)</label>
              <input
                type="number"
                value={formData.sale_price || ''}
                onChange={(e) => setFormData({ ...formData, sale_price: parseInt(e.target.value) || 0 })}
                placeholder="0"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Amount Paid (PKR)</label>
              <input
                type="number"
                value={formData.amount_paid || ''}
                onChange={(e) => setFormData({ ...formData, amount_paid: parseInt(e.target.value) || 0 })}
                placeholder="0"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Mehram (for females) */}
        {formData.gender === 'female' && (
          <div className="bg-white rounded-xl p-4 space-y-4">
            <h3 className="font-medium text-gray-700">Mehram Details (for women under 45)</h3>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Mehram Name</label>
              <input
                type="text"
                value={formData.mehram_name}
                onChange={(e) => setFormData({ ...formData, mehram_name: e.target.value })}
                placeholder="Father/Husband/Brother name"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Relationship</label>
              <select
                value={formData.mehram_relationship}
                onChange={(e) => setFormData({ ...formData, mehram_relationship: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                <option value="husband">Husband</option>
                <option value="father">Father</option>
                <option value="son">Son</option>
                <option value="brother">Brother</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        )}

        {/* Special Requirements */}
        <div className="bg-white rounded-xl p-4">
          <label className="block text-sm text-gray-600 mb-1">Special Requirements</label>
          <textarea
            value={formData.special_requirements}
            onChange={(e) => setFormData({ ...formData, special_requirements: e.target.value })}
            placeholder="Wheelchair, dietary, medical needs..."
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </form>

      {/* Submit Button - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={saving}
          className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl text-white font-medium ${
            saving ? 'bg-primary-300' : 'bg-primary-500'
          }`}
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save size={20} />
              Save Passenger
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default AddPassenger
